import { useMemo, useEffect, useCallback, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router'
import {
  Visible,
  Container,
  Row,
  Col
} from '@leroy-merlin-br/backyard-react-ui'

import emitter from 'garden/src/js/utils/emitter'

import WishlistProviderMethods from 'reactSite/Wishlist/provider'

import { useProfile } from 'catalog/hooks/use-profile'

import {
  HeadTags,
  Shell,
  TopContent,
  BreadcrumbBar,
  FacetsBar,
  Filters,
  Products,
  BottomContent,
  FiltersMobile
} from './components'
import { EntryKeyContextProvider } from './context/EntryKeyContext'
import { buildPath, urlToObject, removeFilterQueries } from './utils/buildUrl'
import {
  onFilterDataLayer,
  onSorterDataLayer,
  onProductImpressionDataLayer
} from './utils/data-layer'
import { getResource } from './services'
import { reducer, initializeState, Constants } from './reducer'
import { Wrapper } from './components/Wrapper'
import { SortersBar } from './components/SortersBar'

const EntryKey = ({
  apiDomain,
  categoryId,
  type,
  url,
  searchType: propSearchType,
  pageType,
  personalizationTrack
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    { propSearchType, url },
    initializeState
  )

  const {
    filters,
    isLoading,
    isLoadingProducts,
    hasError,
    hasHighlightedFilters,
    products,
    metaTags,
    metadata,
    query,
    baseUrl,
    isMobileFilterActive,
    isInitialRender
  } = state

  const { profile } = useProfile()

  const history = useHistory()
  const location = useLocation()

  const breadcrumbRef = useRef()
  const shouldSendFilterDataLayer = useRef(false)
  const previousClick = useRef()

  const isMappedSearch = useMemo(() => type === 'mappedSearch', [type])
  const isSearch = useMemo(() => type === 'search' || isMappedSearch, [
    isMappedSearch,
    type
  ])

  const filterUsedOptions = useMemo(() => {
    const selectedOptions = []
    const usedFilters = Object.values(filters).filter(obj => obj.selected)
    usedFilters.forEach(filter => {
      const indexUsedFilter = filters.indexOf(filter)

      filter.options.forEach((option, optIndex) => {
        selectedOptions.push({
          name: filter.name,
          filterKey: filter.filterKey,
          label: option.label,
          query: option.query,
          filterTypeIndex: indexUsedFilter,
          filterElementIndex: optIndex
        })
      })
    })

    return selectedOptions
  }, [filters])

  const emitErrorsMessage = useCallback(error => {
    emitter.emit('stickyFeedback:error', {
      title: 'Ops! Ocorreu o seguinte erro:',
      content: error
    })
  }, [])

  const setUrl = useCallback(
    newQuery => {
      history.push({ ...buildPath(baseUrl, newQuery, isMappedSearch) })
    },
    [baseUrl, isMappedSearch, history]
  )

  const handleInteraction = useCallback(
    ({ newQuery, resetPage = true, loadOnlyProducts }) => {
      if (resetPage) {
        delete newQuery.page
      }

      setUrl(newQuery)
      dispatch({
        type: Constants.UPDATE_QUERY,
        payload: { newQuery, loadOnlyProducts }
      })
    },
    [setUrl]
  )

  const getProducts = useCallback(async () => {
    try {
      const data = await getResource({
        apiDomain,
        categoryId,
        type,
        query
      })

      dispatch({
        type: Constants.GET_PRODUCTS_SUCCESS,
        payload: { data, isSearch }
      })
    } catch (error) {
      dispatch({ type: Constants.GET_PRODCUTS_FAILURE })

      const errorMessage =
        'Não foi possível carregar os produtos, tente novamente.'

      !isSearch && emitErrorsMessage(errorMessage)
    }
  }, [isSearch, apiDomain, categoryId, type, emitErrorsMessage, query])

  const onOptionClick = useCallback(
    ({ name, value, type, dataLayerOptions }) => {
      scrollToTop()

      const isParamAlreadySet = query[name] && query[name].value === value

      previousClick.current = { isParamAlreadySet, ...dataLayerOptions }

      const newQuery = { ...query }

      if (isParamAlreadySet) {
        delete newQuery[name]
      } else {
        newQuery[name] = { value, type }
      }

      handleInteraction({ newQuery })

      shouldSendFilterDataLayer.current = true
    },
    [scrollToTop, query, handleInteraction]
  )

  const filterByStock = useCallback(
    quantity => {
      const newQuery = {
        ...query,
        minimumStock: { value: quantity, type: 'other' }
      }
      handleInteraction({ newQuery, loadOnlyProducts: true })
    },
    [query, handleInteraction]
  )

  const setPage = useCallback(
    page => {
      scrollToTop()

      const newQuery = { ...query, page: { value: page, type: 'other' } }
      handleInteraction({ newQuery, resetPage: false, loadOnlyProducts: true })
    },
    [scrollToTop, query, handleInteraction]
  )

  const cleanFilters = useCallback(() => {
    const newQuery = removeFilterQueries(query)

    handleInteraction({ newQuery })

    shouldSendFilterDataLayer.current = false
  }, [handleInteraction, query])

  const setQueryFromUrl = useCallback(() => {
    const newQuery = {
      ...urlToObject({ location, url })
    }

    dispatch({
      type: Constants.UPDATE_QUERY,
      payload: { newQuery, resetPage: false }
    })
  }, [location, url])

  const scrollToTop = useCallback(() => {
    document.body.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [])

  const toggleMobileFilter = useCallback(() => {
    dispatch({ type: Constants.TOGGLE_MOBILE_FILTER })
  }, [])

  useEffect(() => {
    if (history.action === 'POP') {
      setQueryFromUrl()
      shouldSendFilterDataLayer.current = false
    }
  }, [history.action, setQueryFromUrl])

  useEffect(() => {
    if (!isInitialRender) {
      getProducts()
    }
  }, [query, isInitialRender, getProducts])

  useEffect(() => {
    if (products.length && !isLoading && !isLoadingProducts) {
      const term = isSearch && query.term ? query.term.value : baseUrl

      onProductImpressionDataLayer({
        products,
        region: profile.region,
        gaTrack: metaTags?.gaTrack,
        type,
        term,
        page: metadata.page
      })

      if (shouldSendFilterDataLayer.current) {
        if (previousClick.current.sorterType) {
          onSorterDataLayer({
            gaTrack: metaTags?.gaTrack,
            ...previousClick.current
          })
        }

        if (previousClick.current.filter) {
          onFilterDataLayer({
            gaTrack: metaTags?.gaTrack,
            pageType,
            ...previousClick.current
          })
        }
      }
    }
  }, [
    products,
    profile,
    metaTags,
    type,
    baseUrl,
    query,
    isLoading,
    isLoadingProducts,
    isSearch,
    metadata,
    pageType
  ])

  const entryKeyContextParams = {
    ...state,
    filterUsedOptions,
    isMappedSearch,
    type,
    isSearch,
    apiDomain,
    categoryId,
    setPage,
    onOptionClick,
    toggleMobileFilter,
    cleanFilters,
    scrollToTop,
    filterByStock,
    profile,
    pageType,
    personalizationTrack:
      personalizationTrack && JSON.parse(personalizationTrack)
  }

  if (isLoading && !isMobileFilterActive) {
    return <Shell />
  }

  if (hasError && isSearch) {
    return (
      <EntryKeyContextProvider value={entryKeyContextParams}>
        <TopContent searchType="error" />
      </EntryKeyContextProvider>
    )
  }

  return (
    <EntryKeyContextProvider value={entryKeyContextParams}>
      <Wrapper breadcrumbRef={breadcrumbRef}>
        <HeadTags />

        <BreadcrumbBar ref={breadcrumbRef} />

        <TopContent />

        <Visible giga tera>
          <SortersBar />
          {hasHighlightedFilters && <FacetsBar />}
        </Visible>

        <Container>
          <Row align="flex-start">
            <Visible giga tera>
              <Col size={{ giga: 3 }}>
                <Filters />
              </Col>
            </Visible>
            <Col size={{ giga: 9, mega: 8, kilo: 4 }}>
              <WishlistProviderMethods>
                <Products />
              </WishlistProviderMethods>
            </Col>
          </Row>

          <BottomContent />
        </Container>

        <FiltersMobile />
      </Wrapper>
    </EntryKeyContextProvider>
  )
}

export default EntryKey

const productPropTypes = {
  id: PropTypes.number.isRequired,
  inWishList: PropTypes.bool.isRequired,
  installments: PropTypes.shape({
    amount: PropTypes.number,
    value: PropTypes.string
  }),
  name: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string
  }),
  picture: PropTypes.string.isRequired,
  price: PropTypes.shape({
    to: PropTypes.shape({
      integers: PropTypes.string.isRequired,
      decimals: PropTypes.string.isRequired
    }).isRequired,
    from: PropTypes.shape({
      integers: PropTypes.string.isRequired,
      decimals: PropTypes.string.isRequired
    })
  }),
  tag: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.string
  }),
  unit: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired
}

const filtersPropTypes = {
  name: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  description: PropTypes.string,
  filterKey: PropTypes.string.isRequired,
  hasExclusiveUrl: PropTypes.bool,
  highlight: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      query: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired
    })
  )
}

EntryKey.propTypes = {
  isLoading: PropTypes.bool,
  isLoadingProducts: PropTypes.bool,
  products: PropTypes.arrayOf(productPropTypes),
  pageInfo: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string
  }),
  filters: PropTypes.arrayOf(filtersPropTypes),
  hasHighlightedFilters: PropTypes.bool,
  sorters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string.isRequired
    })
  ),
  metadata: PropTypes.shape({
    page: PropTypes.number,
    pageCount: PropTypes.number,
    perPage: PropTypes.number,
    totalCount: PropTypes.number
  }),
  metaTags: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string
  }),
  query: PropTypes.shape({
    filter: PropTypes.object,
    order: PropTypes.string,
    page: PropTypes.number
  }),
  url: PropTypes.string,
  searchType: PropTypes.oneOf(['strict', 'fuzzy', 'error'])
}
